import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';

const tones = {
  error: {
    cls: 'border-danger bg-danger-soft text-danger-soft-fg',
    Icon: AlertCircle,
    role: 'alert',
  },
  warning: {
    cls: 'border-warning bg-warning-soft text-warning-soft-fg',
    Icon: TriangleAlert,
    role: 'alert',
  },
  success: {
    cls: 'border-success bg-success-soft text-success-soft-fg',
    Icon: CheckCircle2,
    role: 'status',
  },
  info: { cls: 'border-info bg-info-soft text-info-soft-fg', Icon: Info, role: 'status' },
} as const;

/** Inline message block. Use `tone="error"` for form-level API errors (409 business rules, 403, 429). */
export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: keyof typeof tones;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const { cls, Icon, role } = tones[tone];
  return (
    <div
      role={role}
      className={cn(
        'flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm',
        cls,
        className,
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div>{children}</div>}
      </div>
    </div>
  );
}
